import { ArrowRight, Gamepad2, Mail } from "lucide-react";
import { Link } from "react-router-dom";
import JsonLd from "../components/JsonLd";
import ProjectPanel from "../components/ProjectPanel";
import RadioTuningGame from "../components/RadioTuningGame";
import { Section } from "../components/Section";
import TerminalMiniGame from "../components/TerminalMiniGame";
import { capabilityGroups } from "../data/experience";
import { featuredProjects, releasedProjects } from "../data/projects";
import { site, timeline } from "../data/site";

export default function Home() {
  return (
    <>
      <JsonLd
        id="schema-org"
        data={{
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "SZ Interactive Ltd",
          url: site.baseUrl,
          email: site.email,
          sameAs: [site.instagram],
          foundingDate: "2015",
          foundingLocation: "Turkey",
          address: {
            "@type": "PostalAddress",
            streetAddress: site.address.street,
            addressLocality: site.address.city,
            postalCode: site.address.postalCode,
            addressCountry: site.address.countryCode
          }
        }}
      />
      <section className="hero">
        <div className="hero-media single" aria-hidden="true">
          <img src="/assets/backgrounds/library_hero_1.png" alt="" />
        </div>
        <div className="hero-content">
          <p className="eyebrow">SZ Interactive Ltd</p>
          <h1>Independent games built with character, engineering, and a little chaos.</h1>
          <p className="hero-lede">
            SZ Interactive is an independent game studio established in Turkey in 2015 and based in the United Kingdom since 2019, creating original experiences for PC, mobile, and emerging platforms.
          </p>
          <div className="proof-grid" aria-label="Studio facts">
            <span>Established 2015</span>
            <span>Based in the UK</span>
            <span>75M+ players reached</span>
            <span>PC and mobile development</span>
          </div>
          <div className="action-row hero-actions">
            <Link className="button primary" to="/games">Explore Our Games <ArrowRight size={18} aria-hidden="true" /></Link>
            <Link className="button secondary" to="/experience">View Experience</Link>
          </div>
          <div className="mini-actions">
            {featuredProjects.map((project) => (
              <a key={project.slug} href={project.storeUrl} target="_blank" rel="noreferrer">
                <Gamepad2 size={16} aria-hidden="true" /> {project.title === "JEFF" ? "Wishlist JEFF" : "Wishlist The Shed"}
              </a>
            ))}
          </div>
          <TerminalMiniGame />
        </div>
        <RadioTuningGame />
      </section>

      <Section eyebrow="Featured work" title="Current projects with their own pulse.">
        <div className="featured-stack">
          {featuredProjects.map((project) => <ProjectPanel key={project.slug} project={project} featured />)}
        </div>
      </Section>

      <Section eyebrow="Studio history" title="Built by making, shipping, and moving forward." className="timeline-band">
        <div className="timeline">
          {timeline.map((item) => (
            <article key={`${item.year}-${item.title}`}>
              <span>{item.year}</span>
              <h3>{item.title}</h3>
              <p>{item.text}</p>
            </article>
          ))}
        </div>
      </Section>

      <Section eyebrow="Experience snapshot" title="Hands-on Unity development across the stack.">
        <div className="capability-preview">
          {capabilityGroups.slice(0, 5).map((group) => (
            <article key={group.title} className="capability-card">
              <h3>{group.title}</h3>
              <p>{group.intro}</p>
              <div className="badge-row">{group.items.slice(0, 5).map((item) => <span className="badge" key={item}>{item}</span>)}</div>
            </article>
          ))}
        </div>
        <Link className="text-link section-link" to="/experience">See full experience <ArrowRight size={16} aria-hidden="true" /></Link>
      </Section>

      <Section eyebrow="Released games" title="Mobile roots, physics, puzzles, and shipped work.">
        <div className="project-grid">
          {releasedProjects.map((project) => <ProjectPanel key={project.slug} project={project} />)}
        </div>
      </Section>

      <section className="contact-cta">
        <div>
          <p className="eyebrow">Contact</p>
          <h2>Have a game, role, or collaboration in mind?</h2>
        </div>
        <a className="button primary" href={`mailto:${site.email}`}>
          <Mail size={18} aria-hidden="true" /> {site.email}
        </a>
      </section>
    </>
  );
}
